import React from 'react';
import { EventModel } from './useEventManagement';
import { EVENT_TYPES, PRIVACY_LEVELS, EMPTY_SPONSOR, EMPTY_WAYPOINT } from './eventTypes';

const EventDetailPane: React.FC<{ model: EventModel; onReturnToList: () => void }> = ({ model, onReturnToList }) => {
  const {
    selectedEvent, detailLoading, setSponsorForm, setWaypointForm,
    setShowSponsorForm, setShowWaypointForm, setCancelConfirm, setDeleteConfirm,
    openEditForm, handleRemoveSponsor, handleRemoveWaypoint,
    getStatusBadge, getStatusLabel, formatDate,
  } = model;

  return (
    <>
      {detailLoading ? (
        <div className="rp-bento-card h-full flex items-center justify-center" style={{ minHeight: '200px' }}>
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full mx-auto mb-4"></div>
            <p style={{ color: 'var(--rp-text-secondary)' }}>Loading details...</p>
          </div>
        </div>
      ) : selectedEvent ? (
        <div className="rp-bento-card event-detail-card">
          <button
            type="button"
            onClick={onReturnToList}
            className="events-return admin-btn admin-btn-secondary"
          >
            ← Return to list
          </button>

          {/* Header */}
          <div className="events-detail-head">
            <div className="events-detail-title-wrap">
              <h2 className="events-detail-title" style={{ color: 'var(--rp-text)' }}>{selectedEvent.title}</h2>
              <p className="text-sm mt-1" style={{ color: 'var(--rp-text-secondary)' }}>
                {EVENT_TYPES.find(t => t.value === selectedEvent.event_type)?.label} • {PRIVACY_LEVELS.find(p => p.value === selectedEvent.privacy_level)?.label}
              </p>
            </div>
            <span className={`events-detail-badge px-3 py-1 rounded-full text-xs font-bold border ${getStatusBadge(selectedEvent.status)}`}>
              {getStatusLabel(selectedEvent.status)}
            </span>
          </div>

          {/* Actions */}
          <div className="events-detail-actions">
            <button onClick={() => openEditForm(selectedEvent)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-blue-400/30 text-blue-400 hover:bg-blue-500/10 transition-colors">
              Edit
            </button>
            {selectedEvent.status !== 'cancelled' && selectedEvent.status !== 'completed' && (
              <button onClick={() => setCancelConfirm(selectedEvent.id)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-yellow-400/30 text-yellow-400 hover:bg-yellow-500/10 transition-colors">
                Cancel
              </button>
            )}
            <button onClick={() => setDeleteConfirm(selectedEvent.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-400/30 text-red-400 hover:bg-red-500/10 transition-colors">
              Delete
            </button>
          </div>

          {/* Info Grid */}
          <div className="event-meta text-sm">
            <div>
              <div className="event-meta-label">Host</div>
              <div className="event-meta-value" style={{ color: 'var(--rp-text)' }}>{selectedEvent.host_name || selectedEvent.host_email}</div>
            </div>
            <div>
              <div className="event-meta-label">Created</div>
              <div className="event-meta-value" style={{ color: 'var(--rp-text)' }}>{formatDate(selectedEvent.created_at)}</div>
            </div>
            <div>
              <div className="event-meta-label">Meetup Time</div>
              <div className="event-meta-value" style={{ color: 'var(--rp-text)' }}>{formatDate(selectedEvent.meetup_time)}</div>
            </div>
            <div>
              <div className="event-meta-label">KSU Time</div>
              <div className="event-meta-value" style={{ color: 'var(--rp-text)' }}>{selectedEvent.kick_stands_up_time ? formatDate(selectedEvent.kick_stands_up_time) : '—'}</div>
            </div>
            <div>
              <div className="event-meta-label">Location</div>
              <div className="event-meta-value" style={{ color: 'var(--rp-text)' }}>{selectedEvent.location_name || '—'}</div>
            </div>
            <div>
              <div className="event-meta-label">City</div>
              <div className="event-meta-value" style={{ color: 'var(--rp-text)' }}>{selectedEvent.city && selectedEvent.state ? `${selectedEvent.city}, ${selectedEvent.state}` : '—'}</div>
            </div>
            {selectedEvent.skill_level && (
              <div>
                <div className="event-meta-label">Skill Level</div>
                <div className="event-meta-value" style={{ color: 'var(--rp-text)' }}>{selectedEvent.skill_level}</div>
              </div>
            )}
            {selectedEvent.riding_style && (
              <div>
                <div className="event-meta-label">Riding Style</div>
                <div className="event-meta-value" style={{ color: 'var(--rp-text)' }}>{selectedEvent.riding_style}</div>
              </div>
            )}
            <div>
              <div className="event-meta-label">Views</div>
              <div className="event-meta-value" style={{ color: 'var(--rp-text)' }}>{selectedEvent.view_count}</div>
            </div>
            {selectedEvent.max_participants && (
              <div>
                <div className="event-meta-label">Max Participants</div>
                <div className="event-meta-value" style={{ color: 'var(--rp-text)' }}>{selectedEvent.max_participants}</div>
              </div>
            )}
          </div>

          {selectedEvent.description && (
            <div className="mb-4 p-3 rounded-lg border border-white/5" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
              <div className="text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>Description</div>
              <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--rp-text)' }}>{selectedEvent.description}</p>
            </div>
          )}

          {selectedEvent.cancellation_reason && (
            <div className="mb-4 p-3 rounded-lg border border-red-400/20 bg-red-500/5">
              <div className="text-xs font-semibold mb-1 text-red-400">Cancellation Reason</div>
              <p className="text-sm text-red-300">{selectedEvent.cancellation_reason}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--rp-text-tertiary)' }}>Cancelled: {formatDate(selectedEvent.cancelled_at || '')}</p>
            </div>
          )}

          {/* Participants */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>
              Participants ({selectedEvent.participants?.length || 0})
            </h3>
            {selectedEvent.participants?.length > 0 ? (
              <div className="space-y-1">
                {selectedEvent.participants.map(p => (
                  <div key={p.id} className="flex justify-between items-center text-xs px-2 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                    <span style={{ color: 'var(--rp-text)' }}>{p.name || p.email || 'Unknown'}</span>
                    <div className="flex gap-2 items-center">
                      <span className={`px-2 py-0.5 rounded-full ${
                        p.status === 'checked_in' ? 'bg-green-500/20 text-green-400' :
                        p.status === 'interested' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>{p.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>No participants</p>
            )}
          </div>

          {/* Sponsors */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--rp-text)' }}>
                Sponsors ({selectedEvent.sponsors?.length || 0})
              </h3>
              <button onClick={() => { setSponsorForm(EMPTY_SPONSOR); setShowSponsorForm(true); }}
                className="admin-btn admin-btn-secondary text-xs">+ Add</button>
            </div>
            {selectedEvent.sponsors?.length > 0 ? (
              <div className="space-y-1">
                {selectedEvent.sponsors.map(s => (
                  <div key={s.id} className="flex justify-between items-center text-xs px-2 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: s.fallback_color }}></div>
                      <span style={{ color: 'var(--rp-text)' }}>{s.name}</span>
                      <span style={{ color: 'var(--rp-text-tertiary)' }}>({s.tier})</span>
                    </div>
                    <button onClick={() => handleRemoveSponsor(s.id)} aria-label={`Remove sponsor ${s.name}`} className="admin-icon-btn text-red-400 hover:text-red-300">×</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>No sponsors</p>
            )}
          </div>

          {/* Waypoints */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--rp-text)' }}>
                Waypoints ({selectedEvent.waypoints?.length || 0})
              </h3>
              <button onClick={() => { setWaypointForm({ ...EMPTY_WAYPOINT, order_index: (selectedEvent.waypoints?.length || 0) + 1 }); setShowWaypointForm(true); }}
                className="admin-btn admin-btn-secondary text-xs">+ Add</button>
            </div>
            {selectedEvent.waypoints?.length > 0 ? (
              <div className="space-y-1">
                {selectedEvent.waypoints.map(w => (
                  <div key={w.id} className="flex justify-between items-center text-xs px-2 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                    <div>
                      <span className="font-medium" style={{ color: 'var(--rp-text)' }}>{w.order_index}. {w.name}</span>
                      <span className="ml-2" style={{ color: 'var(--rp-text-tertiary)' }}>({w.waypoint_type})</span>
                    </div>
                    <button onClick={() => handleRemoveWaypoint(w.id)} aria-label={`Remove waypoint ${w.name}`} className="admin-icon-btn text-red-400 hover:text-red-300">×</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>No waypoints</p>
            )}
          </div>
        </div>
      ) : (
        <div className="rp-bento-card h-full flex items-center justify-center" style={{ minHeight: '200px' }}>
          <p style={{ color: 'var(--rp-text-tertiary)' }}>Select an event to view details</p>
        </div>
      )}
    </>
  );
};

export default EventDetailPane;
